import { clsx } from "clsx";
import { ElementType, forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { ControlSize } from "../types";

type FormControlElement = HTMLInputElement | HTMLTextAreaElement;

export interface InputProps extends Omit<
  React.InputHTMLAttributes<FormControlElement>,
  "size"
> {
  error?: boolean;
  size?: ControlSize;
  htmlSize?: number;
  rows?: number;
  as?: "input" | "textarea";
}

const checkStyles = (props: Readonly<InputProps>) => {
  const { type = "checkbox", size = "md" } = props;

  return clsx(
    "transition-all duration-150",
    "appearance-none bg-white border border-gunmetal-200 outline-hidden",
    "checked:bg-primary",
    "focus:ring-[3px] focus:ring-primary/25 focus:border-hunter-300",
    {
      "w-4 h-4": size === "sm",
      "w-5 h-5": size === "md",
      "w-6 h-6": size === "lg",
      "rounded-sm checked:bg-[url('/src/assets/img/checkmark.svg')]":
        type === "checkbox",
      "rounded-full checked:bg-[url('/src/assets/img/radio.svg')]":
        type === "radio",
    },
  );
};

export const inputStyles = (props: Readonly<InputProps>) => {
  const { error: isInvalid, htmlSize, size = "md" } = props;
  return clsx(
    "text-gunmetal-800 enabled:bg-white disabled:bg-gray-200 transition-all",
    "border border-gunmetal-200 rounded-sm outline-hidden",
    "focus:ring-[3px]",
    {
      "w-full": !htmlSize,
      "text-sm py-1 px-2": size === "sm",
      "text-base py-1.5 px-3": size === "md",
      "text-xl py-2 px-4": size === "lg",
      "focus:ring-primary/25 focus:border-hunter-300": !isInvalid,
      "focus:ring-red-400/25 focus:border-red-500": isInvalid,
    },
  );
};

const Input = forwardRef<FormControlElement, InputProps>((props, ref) => {
  const {
    as: Component = "input" as ElementType,
    className,
    htmlSize,
    type,
    error: isInvalid,
    readOnly,
    onChange,
    ...rest
  } = props;

  const classNames =
    type === "checkbox" || type === "radio"
      ? checkStyles(props)
      : inputStyles(props);

  return (
    <Component
      {...rest}
      ref={ref}
      className={twMerge(classNames, className)}
      readOnly={readOnly}
      onChange={readOnly ? undefined : onChange}
      size={htmlSize}
      type={type}
    />
  );
});

export default Input;
